import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/styles.css";
import PageSeo from "../components/PageSeo";
import {
  BRAND_NAME,
  CITY_SERVICE_AREA,
  LEGAL_NAME,
  LOGO_IMAGE,
  SITE_URL,
  SOCIAL_IMAGE,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  absoluteUrl,
} from "../seo/siteData";

function About() {
  const currentYear = new Date().getFullYear();
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: `About ${BRAND_NAME}`,
      url: absoluteUrl("/about"),
      description:
        "Learn about Zarkit, a Jhansi-focused local goods transport and delivery platform for business and personal movement.",
      mainEntity: {
        "@type": "Organization",
        name: BRAND_NAME,
        legalName: LEGAL_NAME,
        url: SITE_URL,
        logo: LOGO_IMAGE,
        image: SOCIAL_IMAGE,
        email: SUPPORT_EMAIL,
        telephone: SUPPORT_PHONE,
        areaServed: CITY_SERVICE_AREA,
      },
    },
  ];

  return (
    <>
      <PageSeo
        title="About Zarkit in Jhansi"
        description="Learn about Zarkit, a local goods transport and delivery platform serving Jhansi with business dispatches, personal shifting, and partner-driver support."
        path="/about"
        keywords="about Zarkit, Zarkit Jhansi, local logistics Jhansi, goods transport platform Jhansi"
        structuredData={structuredData}
      />

      <div className="site-shell">
        <Header />

        <main>
          <div className="about-wrap">

            {/* ===== ABOUT US ===== */}
            <div className="doc-title">About Us – Zarkit</div>

            <div className="intro-block">
              <p>Zarkit is an on-demand logistics and delivery platform designed to make local and intercity goods transportation fast, reliable, and affordable.</p>
              <p>We connect customers with trusted delivery partners through technology to simplify everyday logistics needs.</p>
            </div>

            {/* Who We Are */}
            <h2>Who We Are</h2>
            <p>Zarkit is operated by Zarkit Group, founded with the vision of transforming the traditional delivery system into a smart, technology-driven logistics solution.</p>
            <p>Our platform enables individuals, small businesses, and enterprises to move goods conveniently without owning vehicles or managing delivery staff.</p>

            {/* What We Do */}
            <h2>What We Do</h2>
            <p>Zarkit provides:</p>
            <ul>
              <li>On-demand pickup &amp; delivery services</li>
              <li>Local and short-distance logistics</li>
              <li>Business and personal goods transportation</li>
              <li>Real-time order tracking</li>
              <li>Transparent pricing</li>
              <li>Verified delivery partners</li>
            </ul>
            <p>Whether it's documents, parcels, household items, or business goods, Zarkit makes delivery simple and efficient.</p>

            {/* How Zarkit Works */}
            <h2>How Zarkit Works</h2>
            <ol className="steps-list">
              <li>User places a delivery request via the Zarkit App</li>
              <li>Nearby delivery partner is assigned</li>
              <li>Goods are picked up from the location</li>
              <li>Live tracking is available during transit</li>
              <li>Safe delivery at destination</li>
            </ol>
            <p>Zarkit acts as a technology platform, connecting users with independent delivery partners.</p>

            {/* Our Mission */}
            <h2>Our Mission</h2>
            <div className="info-panel">
              <p>To provide reliable, fast, and affordable logistics services using technology while creating earning opportunities for delivery partners.</p>
            </div>

            {/* Our Vision */}
            <h2>Our Vision</h2>
            <div className="info-panel">
              <p>To become a trusted logistics partner across India, empowering businesses and individuals with seamless delivery solutions.</p>
            </div>

            {/* Our Values */}
            <h2>Our Values</h2>
            <div className="values-grid">
              <div className="value-card">✓ Customer-first approach</div>
              <div className="value-card">✓ Transparency &amp; trust</div>
              <div className="value-card">✓ Safety &amp; compliance</div>
              <div className="value-card">✓ Innovation through technology</div>
              <div className="value-card">✓ Fair opportunities for delivery partners</div>
            </div>

            {/* Company Details */}
            <h2>Company Details</h2>
            <div className="info-panel">
              <ul>
                <li><strong>Brand Name:</strong> Zarkit</li>
                <li><strong>Registered Name:</strong> Zarkit Group</li>
                <li><strong>Owner:</strong> Ravi Kumar</li>
                <li>
                  <strong>Registered Address:</strong><br />
                  Jouri Bujurg, Near Parichha,<br />
                  Jhansi, Uttar Pradesh – 284121, India
                </li>
                <li><strong>Support Email:</strong> {SUPPORT_EMAIL}</li>
                <li><strong>Support Phone:</strong> {SUPPORT_PHONE}</li>
                <li><strong>Service Area:</strong> {CITY_SERVICE_AREA}, Uttar Pradesh</li>
              </ul>
            </div>

            {/* Commitment to Safety */}
            <h2>Commitment to Safety &amp; Compliance</h2>
            <p>Zarkit follows applicable laws, data protection standards, and platform safety policies.</p>
            <p>We continuously improve our services to ensure a secure and reliable experience for all users.</p>

            {/* Contact */}
            <h2>Contact Us</h2>
            <div className="dark-contact">
              <p>For support, feedback, or business inquiries:</p>
              <br />
              <p>✉ <a href="mailto:support@zarkit.com">support@zarkit.com</a></p>
            </div>

            <hr className="section-divider" />

            {/* ===== APP STORE LISTINGS ===== */}
            <div className="doc-subtitle">App Store Listings</div>

            <div className="app-store-grid">
              <div className="app-store-card">
                <div className="app-tag">Customer App</div>
                <h3>Zarkit – Delivery App</h3>
                <p>Zarkit is your trusted on-demand logistics app for quick pickups, safe deliveries, and real-time tracking.</p>
              </div>
              <div className="app-store-card">
                <div className="app-tag">Driver App</div>
                <h3>Zarkit Partner – Driver App</h3>
                <p>Earn by completing delivery trips with Zarkit. Accept orders, navigate easily, and get secure payouts.</p>
              </div>
            </div>

            <hr className="section-divider" />

            {/* ===== CUSTOMER APP ===== */}
            <div className="doc-subtitle">Zarkit – On-Demand Logistics &amp; Delivery App</div>

            <div className="intro-block">
              <p>Zarkit is a smart on-demand logistics and delivery platform designed to make local goods transportation fast, reliable, and affordable. Whether you are an individual, shop owner, or business, Zarkit helps you move goods easily with verified delivery partners and real-time tracking.</p>
            </div>

            <h2>🚚 What Zarkit Offers</h2>
            <ul className="check-list">
              <li>Instant pickup &amp; delivery booking</li>
              <li>Local and short-distance logistics</li>
              <li>Trusted &amp; verified delivery partners</li>
              <li>Real-time order tracking</li>
              <li>Transparent and fair pricing</li>
              <li>Easy booking through mobile app</li>
            </ul>
            <p>From documents and parcels to business goods and household items, Zarkit simplifies your everyday delivery needs.</p>

            <h2>📱 How Zarkit Works</h2>
            <ol className="steps-list">
              <li>Open the Zarkit app</li>
              <li>Enter pickup and drop location</li>
              <li>Get price estimate instantly</li>
              <li>Delivery partner is assigned</li>
              <li>Track your delivery live</li>
              <li>Safe and timely delivery</li>
            </ol>

            <h2>🏠 For Individuals &amp; Businesses</h2>
            <p>Zarkit is perfect for:</p>
            <ul>
              <li>Individuals needing quick local deliveries</li>
              <li>Small shops and vendors</li>
              <li>E-commerce sellers</li>
              <li>Offices and businesses</li>
              <li>Regular or occasional delivery needs</li>
            </ul>
            <p>No need to own vehicles or hire delivery staff — Zarkit handles it all.</p>

            <h2>🔒 Safe &amp; Reliable Platform</h2>
            <ul>
              <li>Verified delivery partners</li>
              <li>Secure in-app payments</li>
              <li>Real-time tracking for transparency</li>
              <li>Customer support assistance</li>
            </ul>
            <p>Your safety, privacy, and trust are our top priorities.</p>

            <h2>💳 Easy Payments</h2>
            <p>Pay securely using:</p>
            <ul>
              <li>UPI</li>
              <li>Debit/Credit Cards</li>
              <li>Other supported digital payment methods</li>
            </ul>
            <p>Zarkit uses trusted third-party payment gateways to ensure secure transactions.</p>

            <h2>📍 Location Access</h2>
            <p>Zarkit uses location data only to:</p>
            <ul>
              <li>Assign nearby delivery partners</li>
              <li>Enable live tracking</li>
              <li>Improve delivery efficiency</li>
            </ul>
            <p>Location access is required only during active service usage.</p>

            <h2>🇮🇳 Built for India</h2>
            <p>Zarkit is built to support India's growing logistics needs, empowering local businesses and creating earning opportunities for delivery partners.</p>

            <h2>📞 Customer Support</h2>
            <div className="dark-contact">
              <p>Need help? Contact us anytime:</p>
              <br />
              <p>✉ <a href="mailto:support@zarkit.com">support@zarkit.com</a></p>
            </div>

            <h2>ℹ️ Company Information</h2>
            <div className="info-panel">
              <ul>
                <li><strong>Brand Name:</strong> Zarkit</li>
                <li><strong>Registered Name:</strong> Zarkit Group</li>
              </ul>
            </div>

            <h2>🔐 Policies &amp; Compliance</h2>
            <p>Zarkit follows applicable laws and platform policies. By using this app, you agree to our:</p>
            <ul>
              <li>Privacy Policy</li>
              <li>Terms &amp; Conditions</li>
              <li>Account Deletion Policy</li>
            </ul>

            <div className="warn-panel">
              <p>🚀 <strong>Download Zarkit Today</strong> — Book deliveries instantly, track orders live, and experience hassle-free logistics with Zarkit.</p>
            </div>

            <hr className="section-divider" />

            {/* ===== DRIVER APP ===== */}
            <div className="doc-subtitle">Zarkit Partner – Driver App</div>

            <div className="intro-block">
              <p>Zarkit Partner is a dedicated app for delivery partners to accept delivery requests, manage trips, and earn money by completing logistics tasks.</p>
              <p>If you own a two-wheeler, three-wheeler, or other suitable vehicle, Zarkit Driver helps you connect with nearby delivery requests easily.</p>
            </div>

            <h2>🚚 What You Can Do with Zarkit Driver App</h2>
            <ul className="check-list">
              <li>Accept nearby delivery requests</li>
              <li>View pickup &amp; drop locations</li>
              <li>Get real-time navigation support</li>
              <li>Track trip earnings and history</li>
              <li>Flexible working hours</li>
              <li>Easy-to-use driver dashboard</li>
            </ul>
            <p>Work when you want and earn on your own schedule.</p>

            <h2>📱 How the Driver App Works</h2>
            <ol className="steps-list">
              <li>Register as a delivery partner</li>
              <li>Complete verification (KYC &amp; vehicle details)</li>
              <li>Go online and receive delivery requests</li>
              <li>Accept orders and pick up goods</li>
              <li>Deliver safely to destination</li>
              <li>Get paid for completed trips</li>
            </ol>

            <h2>🧾 Driver Registration &amp; Verification</h2>
            <p>To ensure safety and service quality, delivery partners must provide:</p>
            <ul>
              <li>Valid mobile number</li>
              <li>Government-issued ID</li>
              <li>Driving license</li>
              <li>Vehicle details</li>
              <li>Bank details for payouts</li>
            </ul>
            <p>All documents are verified before activation.</p>

            <h2>💰 Earnings &amp; Payments</h2>
            <ul>
              <li>Earn per delivery completed</li>
              <li>Transparent earnings breakdown</li>
              <li>Secure payouts via bank transfer</li>
              <li>No fixed working hours</li>
            </ul>
            <p>Actual earnings depend on distance, time, and number of trips completed.</p>

            <h2>📍 Location Access</h2>
            <p>The Zarkit Driver app uses background and foreground location access to:</p>
            <ul>
              <li>Assign nearby delivery requests</li>
              <li>Enable live trip tracking</li>
              <li>Ensure delivery accuracy and safety</li>
            </ul>
            <p>Location access is required only during active duty.</p>

            <h2>🔒 Safety &amp; Compliance</h2>
            <ul>
              <li>Verified users &amp; customers</li>
              <li>In-app support</li>
              <li>Safety guidelines for drivers</li>
              <li>Compliance with local transport laws</li>
            </ul>
            <p>Drivers must follow traffic rules and safety standards at all times.</p>

            <h2>🚫 Driver Responsibilities</h2>
            <p>Delivery partners must:</p>
            <ul>
              <li>Carry valid license and documents</li>
              <li>Ensure vehicle is roadworthy</li>
              <li>Transport only allowed items</li>
              <li>Behave professionally with customers</li>
              <li>Follow Zarkit platform policies</li>
            </ul>
            <p>Violation may result in account suspension.</p>

            <h2>📞 Driver Support</h2>
            <div className="dark-contact">
              <p>For driver-related support or issues:</p>
              <br />
              <p>✉ <a href="mailto:support@zarkit.com">support@zarkit.com</a></p>
            </div>

            <h2>ℹ️ Company Information</h2>
            <div className="info-panel">
              <ul>
                <li><strong>Brand Name:</strong> Zarkit</li>
                <li><strong>Registered Name:</strong> Zarkit Group</li>
              </ul>
            </div>

            <h2>🔐 Policies</h2>
            <p>By using the Zarkit Driver app, you agree to:</p>
            <ul>
              <li>Driver Partner Policy</li>
              <li>Terms &amp; Conditions</li>
              <li>Privacy Policy</li>
            </ul>

            <div className="warn-panel">
              <p>🚀 <strong>Join Zarkit as a Delivery Partner</strong> — Download Zarkit Driver today and start earning by completing delivery tasks near you.</p>
            </div>

            <hr className="section-divider" />

            {/* ===== DRIVER APP ONBOARDING ===== */}
            <div className="doc-subtitle">Driver App – Onboarding Screens</div>

            <div className="onboard-grid">
              {[
                { label: "Screen 1 – Welcome", title: "Welcome to Zarkit Partner", desc: "Earn money by completing delivery tasks near you. Work on your own schedule with Zarkit's on-demand logistics platform." },
                { label: "Screen 2 – Flexible Work", title: "Work When You Want", desc: "Go online anytime, accept delivery requests, and complete trips at your convenience. No fixed working hours." },
                { label: "Screen 3 – Easy Deliveries", title: "Simple & Guided Deliveries", desc: "Get clear pickup and drop locations, in-app navigation, and live trip tracking to complete deliveries smoothly." },
                { label: "Screen 4 – Earnings", title: "Transparent Earnings", desc: "See trip details and earnings clearly in the app. Get paid securely to your registered bank account." },
                { label: "Screen 5 – Location Access", title: "Location Access Required", desc: "We use your location during active duty to assign nearby delivery requests, enable live trip tracking, and ensure accurate and safe deliveries. Location is used only while you are online or on a trip." },
                { label: "Screen 6 – Safety", title: "Safe & Professional Platform", desc: "Deliver only allowed items, follow traffic rules, and maintain professional behavior. Verified users help ensure a safe working environment." },
                { label: "Screen 7 – Get Started", title: "Ready to Start?", desc: "Complete your profile, upload required documents, and go online to receive delivery requests." },
              ].map((screen) => (
                <div className="onboard-card" key={screen.label}>
                  <div className="screen-label">{screen.label}</div>
                  <h3>{screen.title}</h3>
                  <p>{screen.desc}</p>
                </div>
              ))}
            </div>

            <hr className="section-divider" />

            {/* ===== DATA SAFETY – DRIVER APP ===== */}
            <div className="doc-subtitle">🔐 Zarkit Partner – Driver App Data Safety</div>
            <div className="meta">(Play Store)</div>

            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; The app collects user data</p>
              <br />
              <p><span className="badge-no">✗ No</span> &nbsp; The app does NOT sell user data</p>
            </div>

            <h2>📊 Data Collected</h2>

            <h3>1. Personal Information</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Name, Mobile number, Email address, Address</td>
                  <td>Account creation, Identity verification, Driver onboarding, Customer &amp; platform communication</td>
                </tr>
              </tbody>
            </table>

            <h3>2. Location Data</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Precise location (GPS), Background &amp; foreground location (during active duty)</td>
                  <td>Assign nearby delivery requests, Enable live trip tracking, Navigation &amp; route optimization, Safety and fraud prevention</td>
                </tr>
              </tbody>
            </table>
            <div className="warn-panel">
              <p>📍 Location data is collected only when the driver is online or on an active trip.</p>
            </div>

            <h3>3. Financial Information</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Bank account details (for payouts)</td>
                  <td>Driver earnings and payouts</td>
                </tr>
              </tbody>
            </table>
            <div className="warn-panel">
              <p>💳 Card, UPI, or sensitive payment credentials are not stored.</p>
            </div>

            <h3>4. Identity Information</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Government-issued ID (KYC), Driving license, Vehicle details</td>
                  <td>Driver verification, Legal and safety compliance</td>
                </tr>
              </tbody>
            </table>

            <h3>5. App Activity</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>App interactions, Trip history, Earnings history</td>
                  <td>App functionality, Performance monitoring, Dispute resolution</td>
                </tr>
              </tbody>
            </table>

            <h3>6. Device Information</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Device model, OS version, App version, IP address</td>
                  <td>App stability, Crash reporting, Security monitoring</td>
                </tr>
              </tbody>
            </table>

            <h2>🔄 Data Sharing</h2>
            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; Limited data sharing</p>
              <br />
              <p><strong>Shared With:</strong></p>
              <ul>
                <li>Payment service providers (for payouts)</li>
                <li>Cloud hosting providers</li>
                <li>Analytics &amp; crash reporting tools</li>
                <li>Government authorities (when legally required)</li>
              </ul>
              <br />
              <p><span className="badge-no">✗</span> &nbsp; Data is NOT shared for advertising purposes</p>
            </div>

            <h2>🗑️ Data Deletion</h2>
            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; Users can request data deletion</p>
              <br />
              <p>Drivers can request account and data deletion by emailing: <strong>support@zarkit.com</strong></p>
              <p>🕐 Processing time: 7–30 working days</p>
              <p>Some data may be retained as required by law (payments, legal records).</p>
            </div>

            <h2>🔐 Data Security Practices</h2>
            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; Data is encrypted</p>
              <ul>
                <li>Data is transmitted over secure connections</li>
                <li>Access is restricted to authorized personnel</li>
              </ul>
              <br />
              <p><span className="badge-yes">✓ Yes</span> &nbsp; Reasonable security practices are followed</p>
            </div>

            <h2>👶 Children's Data</h2>
            <div className="info-panel">
              <p><span className="badge-no">✗ No</span> &nbsp; The app does not collect data from children</p>
              <p>The Zarkit Driver App is intended for users 18 years and above only.</p>
            </div>

            <h2>📄 Policies Linked in Play Store</h2>
            <ul>
              <li>Privacy Policy</li>
              <li>Terms &amp; Conditions</li>
              <li>Account Deletion Policy</li>
            </ul>

            <hr className="section-divider" />

            {/* ===== DATA SAFETY – CUSTOMER APP ===== */}
            <div className="doc-subtitle">🔐 Zarkit Customer App – Data Safety</div>
            <div className="meta">(Play Store)</div>

            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; The app collects user data</p>
              <br />
              <p><span className="badge-no">✗ No</span> &nbsp; The app does NOT sell user data</p>
            </div>

            <h2>📊 Data Collected</h2>

            <h3>1. Personal Information</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Name, Mobile number, Email address, Address (pickup &amp; delivery)</td>
                  <td>Account creation, Booking deliveries, Customer communication, Service fulfillment</td>
                </tr>
              </tbody>
            </table>

            <h3>2. Location Data</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Precise location (GPS), Approximate location</td>
                  <td>Identify pickup and drop locations, Assign nearby delivery partners, Enable live delivery tracking, Improve service accuracy</td>
                </tr>
              </tbody>
            </table>
            <div className="warn-panel">
              <p>📍 Location data is collected only during active service usage.</p>
            </div>

            <h3>3. Financial Information</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Transaction details (order amount, payment status)</td>
                  <td>Order processing, Payment confirmation, Refund handling</td>
                </tr>
              </tbody>
            </table>
            <div className="warn-panel">
              <p>💳 Sensitive payment data (card number, CVV, UPI PIN) is not stored.</p>
            </div>

            <h3>4. App Activity</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Booking history, App interactions, Preferences</td>
                  <td>App functionality, Customer support, Service improvement</td>
                </tr>
              </tbody>
            </table>

            <h3>5. Device &amp; Technical Information</h3>
            <table className="data-table">
              <thead><tr><th>Collected</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr>
                  <td>Device model, Operating system version, App version, IP address</td>
                  <td>App stability, Bug fixes, Security monitoring</td>
                </tr>
              </tbody>
            </table>

            <h2>🔄 Data Sharing</h2>
            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; Limited sharing</p>
              <br />
              <p><strong>Shared With:</strong></p>
              <ul>
                <li>Delivery partners (pickup &amp; drop details, contact number)</li>
                <li>Payment gateways (for payment processing)</li>
                <li>Cloud hosting providers</li>
                <li>Analytics and crash reporting tools</li>
                <li>Government authorities (when legally required)</li>
              </ul>
              <br />
              <p><span className="badge-no">✗</span> &nbsp; User data is NOT shared for advertising purposes</p>
            </div>

            <h2>🗑️ Data Deletion</h2>
            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; Users can request account &amp; data deletion</p>
              <br />
              <p>Users can request deletion by:</p>
              <ul>
                <li>Emailing <strong>support@zarkit.com</strong></li>
                <li>Using in-app account deletion (if available)</li>
              </ul>
              <p>🕐 Processing time: 7–30 working days</p>
              <p>Some data may be retained as required by law (transactions, tax, disputes).</p>
            </div>

            <h2>🔐 Data Security</h2>
            <div className="info-panel">
              <p><span className="badge-yes">✓ Yes</span> &nbsp; User data is encrypted</p>
              <ul>
                <li>Secure data transmission</li>
                <li>Restricted internal access</li>
              </ul>
              <br />
              <p><span className="badge-yes">✓ Yes</span> &nbsp; Reasonable security practices are implemented</p>
            </div>

            <h2>👶 Children's Data</h2>
            <div className="info-panel">
              <p><span className="badge-no">✗ No</span> &nbsp; The app does not collect children's data</p>
              <p>The Zarkit Customer App is intended for users 13 years and above.</p>
              <p>No data is knowingly collected from children under 13.</p>
            </div>

            <h2>📄 Policies to Link in Play Store</h2>
            <ul>
              <li>Privacy Policy</li>
              <li>Terms &amp; Conditions</li>
              <li>Account Deletion Policy</li>
            </ul>

          </div>
        </main>

        <Footer currentYear={currentYear} />
      </div>
    </>
  );
}

export default About;
